/**
 * Swing variance system — adds realistic jitter to SwingParams
 * based on player handicap. Higher handicap = more variance.
 *
 * Uses Gaussian distribution (Box-Muller) for natural spread.
 * Provisional skill priors, checked against repeated training-set TrackMan
 * groups. The archive has no verified tour/handicap labels; these are not
 * empirically fitted handicap distributions. See docs/swing-dispersion.md.
 *
 * Includes off-center strike model — amateurs miss the sweet spot
 * more often, losing ball speed and gaining unwanted spin/tilt.
 */

import type { SwingParams } from '../types';
import { deriveLaunchDirection, deriveSpinAxisFromImpact } from './SwingInput';

/** Gaussian random using Box-Muller transform. */
function gaussianRandom(mean: number, stdDev: number, random = Math.random): number {
  const u1 = 1 - random(); // (0, 1], so log(0) cannot create infinite jitter
  const u2 = random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return mean + z * stdDev;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Variance profile — standard deviations for each parameter.
 */
export interface VarianceProfile {
  clubSpeedStdDev: number;     // mph
  ballSpeedStdDev: number;     // mph
  launchAngleStdDev: number;   // degrees
  spinRateStdDev: number;      // rpm
  launchDirStdDev: number;     // degrees
  spinAxisStdDev: number;      // degrees
  attackAngleStdDev: number;   // degrees
  faceAngleStdDev: number;     // degrees
  clubPathStdDev: number;      // degrees
  facePathCorrelation?: number; // shared delivery variation, [-1, 1]
}

type ImpactDelivery = Pick<SwingParams, 'launchAngle' | 'attackAngle' | 'faceAngle' | 'clubPath'>;

/**
 * Translate a changed club delivery into ball-flight direction while retaining
 * the measured launch direction and spin axis as the baseline truth.
 */
export function coupleDeliveryToFlight(
  base: SwingParams,
  varied: ImpactDelivery,
): Pick<SwingParams, 'launchDirection' | 'spinAxis'> {
  const baseModeledLaunch = deriveLaunchDirection(
    base.faceAngle, base.clubPath, base.launchAngle, base.attackAngle,
  );
  const variedModeledLaunch = deriveLaunchDirection(
    varied.faceAngle, varied.clubPath, varied.launchAngle, varied.attackAngle,
  );
  const baseModeledAxis = deriveSpinAxisFromImpact(
    base.faceAngle, base.clubPath, base.launchAngle, base.attackAngle,
  );
  const variedModeledAxis = deriveSpinAxisFromImpact(
    varied.faceAngle, varied.clubPath, varied.launchAngle, varied.attackAngle,
  );
  return {
    launchDirection: base.launchDirection + (variedModeledLaunch - baseModeledLaunch),
    spinAxis: base.spinAxis + (variedModeledAxis - baseModeledAxis),
  };
}

/**
 * Compute variance profile from handicap.
 *   0 hcp -> skilled delivery, 25 hcp -> recreational delivery.
 * Face/path correlation is an explicit modeling assumption, not a tour-data fit.
 */
export function getVarianceProfile(handicap: number): VarianceProfile {
  const t = Math.max(0, Math.min(25, handicap)) / 25;
  return {
    clubSpeedStdDev:    lerp(0.8, 5.0, t),
    ballSpeedStdDev:    lerp(1.2, 7.0, t),
    launchAngleStdDev:  lerp(0.5, 2.5, t),
    spinRateStdDev:     lerp(150, 1000, t),
    launchDirStdDev:    lerp(0.8, 3.0, t),
    spinAxisStdDev:     lerp(2.0, 6.0, t),
    attackAngleStdDev:  lerp(0.5, 3.0, t),
    faceAngleStdDev:    lerp(1.0, 3.0, t),
    clubPathStdDev:     lerp(0.8, 2.6, t),
    facePathCorrelation: lerp(0.45, 0.15, t),
  };
}

// ---------------------------------------------------------------------------
// Off-center strike model
// ---------------------------------------------------------------------------

/**
 * Apply off-center strike effects to swing params.
 *
 * Model:
 *  - Miss distance from sweet spot is Gaussian with sigma scaling by handicap.
 *  - Off-center hits lose ball speed (quadratic in miss distance).
 *  - Off-center hits tilt the spin axis (gear effect).
 *  - Heel/toe misses add or subtract spin (gear effect).
 *
 * @param params  The swing params (already jittered by applyVariance).
 * @param handicap  Player handicap (0-25).
 * @returns  A new SwingParams with strike effects applied.
 */
export function applyStrikeVariance(
  params: SwingParams,
  handicap: number,
  random = Math.random,
): SwingParams {
  const t = Math.max(0, Math.min(25, handicap)) / 25;

  // Miss distance from center (inches), Gaussian with handicap-scaled sigma
  const missSigma = lerp(0.15, 0.6, t);
  const missX = gaussianRandom(0, missSigma, random); // heel(-) / toe(+)
  const missY = gaussianRandom(0, missSigma, random); // low(-) / high(+)
  const missDistance = Math.sqrt(missX * missX + missY * missY);

  // 1) Dynamic smash factor from strike quality
  //    Center hit: full smash (1.49 driver). Off-center: quadratic degradation.
  //    At 0.5" miss (typical 15-hcp): lose ~0.9 mph. At 1.0": lose ~3.5 mph.
  //    Additionally, vertical misses (high/low on face) affect launch angle.
  const speedLoss = missDistance * missDistance * 3.5;
  const adjustedBallSpeed = Math.max(0, params.ballSpeed - speedLoss);

  // Vertical miss shifts launch: high on face = higher launch, low = lower
  const launchShift = missY * 1.5; // degrees per inch of vertical miss

  // 2) Spin axis tilt — 4.0 degrees per inch, random direction from miss vector
  //    Toe contributes draw (negative axis); heel contributes fade (positive).
  const axisTilt = -missX * 4.0;
  const adjustedSpinAxis = params.spinAxis + axisTilt;

  // 3) Gear effect spin change — 200 rpm per inch of heel/toe miss
  //    Toe hit adds draw spin (negative rpm change for RH), heel adds fade spin
  const spinChange = -missX * 200;
  const adjustedSpinRate = Math.max(0, params.spinRate + spinChange);

  return {
    ...params,
    ballSpeed: adjustedBallSpeed,
    launchAngle: params.launchAngle + launchShift,
    spinAxis: adjustedSpinAxis,
    spinRate: adjustedSpinRate,
  };
}

/**
 * Apply variance to a SwingParams, returning a jittered copy.
 * Also applies off-center strike effects based on handicap.
 */
export function applyVariance(
  params: SwingParams,
  profile: VarianceProfile,
  handicap: number = 15,
  random = Math.random,
): SwingParams {
  // Step 1: Vary the physical delivery. Face and path must drive start line
  // and curvature together; randomizing all four independently can show an
  // open face that flies straight (or a neutral face that mysteriously slices).
  const launchAngle = gaussianRandom(params.launchAngle, profile.launchAngleStdDev, random);
  const attackAngle = gaussianRandom(params.attackAngle, profile.attackAngleStdDev, random);
  const pathNoise = gaussianRandom(0, 1, random);
  const faceNoise = gaussianRandom(0, 1, random);
  const rho = Math.max(-1, Math.min(1, profile.facePathCorrelation ?? 0));
  const clubPath = params.clubPath + pathNoise * profile.clubPathStdDev;
  const faceAngle = params.faceAngle + profile.faceAngleStdDev
    * (rho * pathNoise + Math.sqrt(1 - rho * rho) * faceNoise);
  const directional = coupleDeliveryToFlight(params, {
    faceAngle, clubPath, launchAngle, attackAngle,
  });

  // Preserve measured launch/spin-axis as the center of the distribution, then
  // add the D-plane delta from this particular delivery. Small residual terms
  // represent measurement/strike effects not explained by face and path.
  const jittered: SwingParams = {
    ...params,
    clubSpeed: Math.max(0, gaussianRandom(params.clubSpeed, profile.clubSpeedStdDev, random)),
    ballSpeed: Math.max(0, gaussianRandom(params.ballSpeed, profile.ballSpeedStdDev, random)),
    launchAngle,
    launchDirection: directional.launchDirection + gaussianRandom(0, profile.launchDirStdDev * 0.2, random),
    spinRate: Math.max(0, gaussianRandom(params.spinRate, profile.spinRateStdDev, random)),
    spinAxis: directional.spinAxis + gaussianRandom(0, profile.spinAxisStdDev * 0.2, random),
    attackAngle,
    faceAngle,
    clubPath,
  };

  // Step 2: Apply off-center strike effects
  return applyStrikeVariance(jittered, handicap, random);
}

/**
 * Generate N jittered variants of the base params.
 */
export function generateVariantParams(
  base: SwingParams,
  handicap: number,
  count: number,
  random = Math.random,
): SwingParams[] {
  const profile = getVarianceProfile(handicap);
  return Array.from({ length: count }, () => applyVariance(base, profile, handicap, random));
}
